import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Music, Users, CheckCircle, ArrowRight, X } from 'lucide-react';

const BandSignupPage: React.FC = () => {
  const [formData, setFormData] = useState({
    // Band Director Information
    directorName: '',
    directorEmail: '',
    directorPhone: '',
    
    // School Information
    schoolName: '',
    schoolAddress: '',
    schoolCity: '',
    schoolState: '',
    schoolZip: '',
    schoolPhone: '',
    schoolWebsite: '',
    
    // Band Information
    bandTypes: {
      marching: false,
      concert: false,
      jazz: false,
      orchestra: false,
      choir: false,
      mariachi: false,
      other: false
    },
    bandSize: '',
    bandLogo: null as File | null,
    schoolColors: ['#000000', '#000000', '#000000'],
    
    // Agreement
    agreeToTerms: false,
    agreeToCommission: false,
    agreeToGuidelines: false
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({
      ...prev,
      bandLogo: file
    }));
  };

  const handleColorChange = (index: number, color: string) => {
    setFormData(prev => ({
      ...prev,
      schoolColors: prev.schoolColors.map((c, i) => i === index ? color : c)
    }));
  };

  const handleBandTypeChange = (bandType: string) => {
    setFormData(prev => ({
      ...prev,
      bandTypes: {
        ...prev.bandTypes,
        [bandType]: !prev.bandTypes[bandType as keyof typeof prev.bandTypes]
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit to backend API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/band-applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // Band Director Information
          directorName: formData.directorName,
          directorEmail: formData.directorEmail,
          directorPhone: formData.directorPhone,
          
          // School Information
          schoolName: formData.schoolName,
          schoolAddress: formData.schoolAddress,
          schoolCity: formData.schoolCity,
          schoolState: formData.schoolState,
          schoolZip: formData.schoolZip,
          schoolPhone: formData.schoolPhone,
          schoolWebsite: formData.schoolWebsite,
          
          // Band Information
          bandTypes: formData.bandTypes,
          bandSize: formData.bandSize,
          schoolColors: formData.schoolColors,
          
          // Agreements
          agreeToTerms: formData.agreeToTerms,
          agreeToCommission: formData.agreeToCommission,
          agreeToGuidelines: formData.agreeToGuidelines
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Send notification email to support@bandcam.io
        try {
          await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/email/band-application`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              directorName: formData.directorName,
              directorEmail: formData.directorEmail,
              directorPhone: formData.directorPhone,
              schoolName: formData.schoolName,
              schoolAddress: formData.schoolAddress,
              schoolCity: formData.schoolCity,
              schoolState: formData.schoolState,
              schoolZip: formData.schoolZip,
              schoolPhone: formData.schoolPhone,
              schoolWebsite: formData.schoolWebsite,
              bandTypes: formData.bandTypes,
              bandSize: formData.bandSize,
              schoolColors: formData.schoolColors,
              agreeToTerms: formData.agreeToTerms,
              agreeToCommission: formData.agreeToCommission,
              agreeToGuidelines: formData.agreeToGuidelines
            })
          });
        } catch (emailError) {
          console.error('Failed to send notification email:', emailError);
          // Don't fail the entire submission if email fails
        }

        alert('Thank you for your application! We will review it and contact you within 24-48 hours.');
        // Reset form
        setFormData({
          directorName: '',
          directorEmail: '',
          directorPhone: '',
          schoolName: '',
          schoolAddress: '',
          schoolCity: '',
          schoolState: '',
          schoolZip: '',
          schoolPhone: '',
          schoolWebsite: '',
          bandTypes: {
            marching: false,
            concert: false,
            jazz: false,
            orchestra: false,
            choir: false,
            mariachi: false,
            other: false
          },
          bandSize: '',
          bandLogo: null,
          schoolColors: ['#000000', '#000000', '#000000'],
          agreeToTerms: false,
          agreeToCommission: false,
          agreeToGuidelines: false
        });
        setCurrentStep(1);
      } else {
        throw new Error(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 1:
        return formData.directorName && formData.directorEmail && formData.directorPhone;
      case 2:
        return formData.schoolName && formData.schoolAddress && formData.schoolCity && formData.schoolState;
      case 3:
        return Object.values(formData.bandTypes).some(type => type) && formData.bandSize;
      case 4:
        return formData.agreeToTerms && formData.agreeToCommission && formData.agreeToGuidelines;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <header className="bg-sky border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="/logo.png" 
                alt="BandCam.io Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="ml-2 text-xl font-bold text-black">BandCam.io</span>
            </div>
            <Link
              to="/band-directors"
              className="btn-primary flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Band Directors
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-midnight text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">
            Sign Up Your Band
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Join hundreds of bands already earning money with BandCam.io. 
            Keep 85% of every video sale - completely free to join.
          </p>
          
          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-4">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    currentStep >= step 
                      ? 'bg-goldie text-black border-goldie' 
                      : 'border-gray-600 text-gray-400'
                  }`}>
                    {step}
                  </div>
                  {step < 4 && (
                    <div className={`w-8 h-0.5 mx-2 ${
                      currentStep > step ? 'bg-goldie' : 'bg-gray-600'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg border-2 border-black">
            
            {/* Step 1: Band Director Information */}
            {currentStep === 1 && (
              <div>
                <h2 className="text-2xl font-bold mb-6 font-display">Band Director Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="directorName" className="block text-sm font-semibold mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="directorName"
                      name="directorName"
                      value={formData.directorName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="directorEmail" className="block text-sm font-semibold mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="directorEmail"
                      name="directorEmail"
                      value={formData.directorEmail}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="your.email@school.edu"
                    />
                  </div>
                  <div>
                    <label htmlFor="directorPhone" className="block text-sm font-semibold mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="directorPhone"
                      name="directorPhone"
                      value={formData.directorPhone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: School Information */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-2xl font-bold mb-6 font-display">School Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label htmlFor="schoolName" className="block text-sm font-semibold mb-2">
                      School Name *
                    </label>
                    <input
                      type="text"
                      id="schoolName"
                      name="schoolName"
                      value={formData.schoolName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="Your school name"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="schoolAddress" className="block text-sm font-semibold mb-2">
                      School Address *
                    </label>
                    <input
                      type="text"
                      id="schoolAddress"
                      name="schoolAddress"
                      value={formData.schoolAddress}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <label htmlFor="schoolCity" className="block text-sm font-semibold mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      id="schoolCity"
                      name="schoolCity"
                      value={formData.schoolCity}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label htmlFor="schoolState" className="block text-sm font-semibold mb-2">
                      State *
                    </label>
                    <input
                      type="text"
                      id="schoolState"
                      name="schoolState"
                      value={formData.schoolState}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <label htmlFor="schoolZip" className="block text-sm font-semibold mb-2">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      id="schoolZip"
                      name="schoolZip"
                      value={formData.schoolZip}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="ZIP Code"
                    />
                  </div>
                  <div>
                    <label htmlFor="schoolPhone" className="block text-sm font-semibold mb-2">
                      School Phone
                    </label>
                    <input
                      type="tel"
                      id="schoolPhone"
                      name="schoolPhone"
                      value={formData.schoolPhone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="School phone number"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="schoolWebsite" className="block text-sm font-semibold mb-2">
                      School Website
                    </label>
                    <input
                      type="url"
                      id="schoolWebsite"
                      name="schoolWebsite"
                      value={formData.schoolWebsite}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                      placeholder="https://www.yourschool.edu"
                    />
                  </div>
                </div>
              </div>
            )}

                         {/* Step 3: Band Information */}
             {currentStep === 3 && (
               <div>
                 <h2 className="text-2xl font-bold mb-6 font-display">Band Information</h2>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <div>
                     <label className="block text-sm font-semibold mb-2">
                       Band Type(s) *
                     </label>
                     <div className="space-y-3">
                       <label className="flex items-center space-x-3">
                         <input
                           type="checkbox"
                           checked={formData.bandTypes.marching}
                           onChange={() => handleBandTypeChange('marching')}
                           className="w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                         />
                         <span className="text-sm">Marching Band</span>
                       </label>
                       <label className="flex items-center space-x-3">
                         <input
                           type="checkbox"
                           checked={formData.bandTypes.concert}
                           onChange={() => handleBandTypeChange('concert')}
                           className="w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                         />
                         <span className="text-sm">Concert Band</span>
                       </label>
                       <label className="flex items-center space-x-3">
                         <input
                           type="checkbox"
                           checked={formData.bandTypes.jazz}
                           onChange={() => handleBandTypeChange('jazz')}
                           className="w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                         />
                         <span className="text-sm">Jazz Band</span>
                       </label>
                       <label className="flex items-center space-x-3">
                         <input
                           type="checkbox"
                           checked={formData.bandTypes.orchestra}
                           onChange={() => handleBandTypeChange('orchestra')}
                           className="w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                         />
                         <span className="text-sm">Orchestra</span>
                       </label>
                       <label className="flex items-center space-x-3">
                         <input
                           type="checkbox"
                           checked={formData.bandTypes.choir}
                           onChange={() => handleBandTypeChange('choir')}
                           className="w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                         />
                         <span className="text-sm">Choir</span>
                       </label>
                       <label className="flex items-center space-x-3">
                         <input
                           type="checkbox"
                           checked={formData.bandTypes.mariachi}
                           onChange={() => handleBandTypeChange('mariachi')}
                           className="w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                         />
                         <span className="text-sm">Mariachi Band</span>
                       </label>
                       <label className="flex items-center space-x-3">
                         <input
                           type="checkbox"
                           checked={formData.bandTypes.other}
                           onChange={() => handleBandTypeChange('other')}
                           className="w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                         />
                         <span className="text-sm">Other</span>
                       </label>
                     </div>
                   </div>
                                     <div>
                     <label htmlFor="bandSize" className="block text-sm font-semibold mb-2">
                       Band Size *
                     </label>
                     <select
                       id="bandSize"
                       name="bandSize"
                       value={formData.bandSize}
                       onChange={handleChange}
                       required
                       className="w-full px-4 py-3 border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
                     >
                       <option value="">Select size</option>
                       <option value="small">Small (&lt; 50 students)</option>
                       <option value="medium">Medium (50-119 students)</option>
                       <option value="large">Large (120-199 students)</option>
                       <option value="very-large">Very Large (200+ students)</option>
                     </select>
                   </div>
                   <div>
                     <label htmlFor="bandLogo" className="block text-sm font-semibold mb-2">
                       Band Logo
                     </label>
                     <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-goldie transition-colors">
                       <input
                         type="file"
                         id="bandLogo"
                         name="bandLogo"
                         onChange={handleFileChange}
                         accept="image/*"
                         className="hidden"
                       />
                       <label htmlFor="bandLogo" className="cursor-pointer">
                         {formData.bandLogo ? (
                           <div className="space-y-2">
                             <img 
                               src={URL.createObjectURL(formData.bandLogo)} 
                               alt="Band Logo Preview" 
                               className="w-32 h-32 object-contain mx-auto border-2 border-gray-200 rounded-lg"
                             />
                             <p className="text-sm text-gray-600">{formData.bandLogo.name}</p>
                             <p className="text-xs text-gray-500">Click to change</p>
                           </div>
                         ) : (
                           <div className="space-y-2">
                             <div className="w-32 h-32 bg-gray-100 border-2 border-gray-200 rounded-lg mx-auto flex items-center justify-center">
                               <span className="text-gray-400 text-4xl">🎵</span>
                             </div>
                             <p className="text-sm text-gray-600">Click to upload band logo</p>
                             <p className="text-xs text-gray-500">PNG, JPG, or GIF up to 5MB</p>
                           </div>
                         )}
                       </label>
                     </div>
                   </div>
                   <div className="md:col-span-2">
                     <label className="block text-sm font-semibold mb-2">
                       School Colors
                     </label>
                     <div className="grid grid-cols-3 gap-4">
                       {formData.schoolColors.map((color, index) => (
                         <div key={index} className="text-center">
                           <label className="block text-xs font-medium mb-2">
                             Color {index + 1}
                           </label>
                           <div className="flex items-center space-x-2">
                             <input
                               type="color"
                               value={color}
                               onChange={(e) => handleColorChange(index, e.target.value)}
                               className="w-12 h-12 border-2 border-black rounded-lg cursor-pointer"
                             />
                             <input
                               type="text"
                               value={color}
                               onChange={(e) => handleColorChange(index, e.target.value)}
                               className="flex-1 px-3 py-2 border-2 border-black rounded-lg text-sm font-mono"
                               placeholder="#000000"
                             />
                           </div>
                         </div>
                       ))}
                     </div>
                     <p className="text-xs text-gray-600 mt-2">
                       Choose up to 3 colors that represent your school. These will be used for branding and customization.
                     </p>
                   </div>
                </div>
              </div>
            )}

            {/* Step 4: Agreement */}
            {currentStep === 4 && (
              <div>
                <h2 className="text-2xl font-bold mb-6 font-display">Agreement & Terms</h2>
                <div className="space-y-6">
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <h3 className="text-lg font-semibold mb-4 text-goldie">Commission Structure</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>• You keep 85% of every video sale from your school</li>
                      <li>• Payments are sent monthly with no minimum threshold</li>
                      <li>• No setup fees, monthly fees, or hidden costs</li>
                      <li>• You can withdraw from the program at any time</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-4">
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                        required
                        className="mt-1 w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                      />
                                             <span className="text-sm">
                         I agree to the <button type="button" onClick={() => setShowTermsModal(true)} className="text-goldie hover:underline">Terms of Service</button> and 
                         <button type="button" onClick={() => setShowPrivacyModal(true)} className="text-goldie hover:underline"> Privacy Policy</button> *
                       </span>
                    </label>
                    
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="agreeToCommission"
                        checked={formData.agreeToCommission}
                        onChange={handleChange}
                        required
                        className="mt-1 w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                      />
                      <span className="text-sm">
                        I understand and agree to the 85% commission structure and payment terms *
                      </span>
                    </label>
                    
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="agreeToGuidelines"
                        checked={formData.agreeToGuidelines}
                        onChange={handleChange}
                        required
                        className="mt-1 w-4 h-4 text-goldie border-2 border-black rounded focus:ring-goldie"
                      />
                      <span className="text-sm">
                        I agree to follow BandCam.io's recording guidelines and quality standards *
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t-2 border-gray-200">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="bg-transparent text-black border-2 border-black hover:bg-black hover:text-white px-6 py-3 font-bold rounded-lg"
                >
                  Previous
                </button>
              )}
              
              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!isStepValid(currentStep)}
                  className={`ml-auto px-6 py-3 font-bold rounded-lg flex items-center ${
                    isStepValid(currentStep)
                      ? 'bg-goldie text-black hover:bg-opacity-90 border-2 border-black'
                      : 'bg-gray-300 text-gray-500 border-2 border-gray-300 cursor-not-allowed'
                  }`}
                >
                  Next
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!isStepValid(currentStep) || isSubmitting}
                  className={`ml-auto px-8 py-3 font-bold rounded-lg flex items-center ${
                    isStepValid(currentStep) && !isSubmitting
                      ? 'bg-goldie text-black hover:bg-opacity-90 border-2 border-black'
                      : 'bg-gray-300 text-gray-500 border-2 border-gray-300 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Benefits Reminder Section */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8 font-display">Why Join BandCam.io?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Music className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-2">Keep 85%</h3>
              <p className="text-gray-300">
                You keep 85% of every video sale. No hidden fees or monthly costs.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-2">Easy Setup</h3>
              <p className="text-gray-300">
                Use your existing equipment. We provide all the support you need.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-2">Flexible Schedule</h3>
              <p className="text-gray-300">
                Record videos on your own time. No strict deadlines or requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight text-white border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="BandCam.io Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-2 text-xl font-bold">BandCam.io</span>
              </div>
              <p className="text-gray-300 mb-4">
                Connecting families with their school bands to create unforgettable personalized video messages for special occasions.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/schools" className="text-gray-300 hover:text-white">Find Your School</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
                <li><Link to="/faq" className="text-gray-300 hover:text-white">FAQ</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BandCam.io. All rights reserved.</p>
          </div>
                 </div>
       </footer>

       {/* Terms of Service Modal */}
       {showTermsModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
             <div className="sticky top-0 bg-white border-b-2 border-gray-200 p-6 flex justify-between items-center">
               <h2 className="text-2xl font-bold font-display">Terms of Service</h2>
               <button
                 onClick={() => setShowTermsModal(false)}
                 className="text-gray-500 hover:text-gray-700"
               >
                 <X className="w-6 h-6" />
               </button>
             </div>
             <div className="p-6 space-y-6">
               <div>
                 <h3 className="text-xl font-bold mb-4">1. Introduction</h3>
                 <p className="text-gray-700 mb-4">
                   Welcome to BandCam.io. These Terms of Service ("Terms") govern your use of our website and services 
                   that connect school bands with families seeking personalized video messages for special occasions.
                 </p>
                 <p className="text-gray-700">
                   By accessing or using our service, you agree to be bound by these Terms. If you disagree with any 
                   part of these terms, you may not access our service.
                 </p>
               </div>

               <div>
                 <h3 className="text-xl font-bold mb-4">2. Service Description</h3>
                 <p className="text-gray-700 mb-4">
                   BandCam.io provides a platform that:
                 </p>
                 <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                   <li>Connects families with school bands for personalized video messages</li>
                   <li>Facilitates the creation and delivery of custom musical performances</li>
                   <li>Processes payments and manages commission distribution</li>
                   <li>Provides customer support and quality assurance</li>
                 </ul>
               </div>

               <div>
                 <h3 className="text-xl font-bold mb-4">3. Payment and Commission Terms</h3>
                 <p className="text-gray-700 mb-4">
                   <strong>Commission Structure:</strong> Band directors keep 85% of each video sale, with BandCam.io 
                   retaining 15% for platform services, payment processing, and customer support.
                 </p>
                 <p className="text-gray-700 mb-4">
                   <strong>Payment Schedule:</strong> Commissions are calculated monthly and paid within 15 days of 
                   the end of each month. No minimum threshold applies.
                 </p>
                 <p className="text-gray-700">
                   <strong>Refunds:</strong> Customers may request refunds within 7 days of video delivery if the 
                   video does not meet our quality standards or delivery requirements.
                 </p>
               </div>

               <div>
                 <h3 className="text-xl font-bold mb-4">4. Band Director Responsibilities</h3>
                 <p className="text-gray-700 mb-4">
                   As a band director participating in our service, you agree to:
                 </p>
                 <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                   <li>Maintain appropriate permissions from your school administration</li>
                   <li>Ensure all student participants have proper consent forms</li>
                   <li>Deliver high-quality video recordings within specified timeframes</li>
                   <li>Follow our recording guidelines and quality standards</li>
                   <li>Maintain professional conduct and appropriate content</li>
                   <li>Respect copyright laws and obtain necessary licenses for music performed</li>
                 </ul>
               </div>

               <div>
                 <h3 className="text-xl font-bold mb-4">5. Contact Information</h3>
                 <p className="text-gray-700 mb-4">
                   If you have any questions about these Terms of Service, please contact us:
                 </p>
                 <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                   <p className="text-gray-700"><strong>Email:</strong> legal@bandcam.io</p>
                   <p className="text-gray-700"><strong>Phone:</strong> (555) 123-4567</p>
                   <p className="text-gray-700"><strong>Address:</strong> 123 Music Street, Suite 100, Anytown, ST 12345</p>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Privacy Policy Modal */}
       {showPrivacyModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
             <div className="sticky top-0 bg-white border-b-2 border-gray-200 p-6 flex justify-between items-center">
               <h2 className="text-2xl font-bold font-display">Privacy Policy</h2>
               <button
                 onClick={() => setShowPrivacyModal(false)}
                 className="text-gray-500 hover:text-gray-700"
               >
                 <X className="w-6 h-6" />
               </button>
             </div>
             <div className="p-6 space-y-6">
               <div>
                 <h3 className="text-xl font-bold mb-4">1. Introduction</h3>
                 <p className="text-gray-700 mb-4">
                   BandCam.io ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                   explains how we collect, use, disclose, and safeguard your information when you use our platform 
                   that connects school bands with families seeking personalized video messages.
                 </p>
                 <p className="text-gray-700">
                   By using our service, you consent to the data practices described in this policy. If you do not 
                   agree with our policies and practices, please do not use our service.
                 </p>
               </div>

               <div>
                 <h3 className="text-xl font-bold mb-4">2. Information We Collect</h3>
                 <p className="text-gray-700 mb-4">
                   We collect information you provide directly to us, including:
                 </p>
                 <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-6">
                   <li><strong>Account Information:</strong> Name, email address, phone number, and password</li>
                   <li><strong>Profile Information:</strong> School name, address, band type, and size</li>
                   <li><strong>Order Information:</strong> Video requests, personalized messages, and payment details</li>
                   <li><strong>Communication:</strong> Messages, feedback, and support requests</li>
                 </ul>
                 <p className="text-gray-700">
                   We also automatically collect device information, usage data, and technical data when you use our service.
                 </p>
               </div>

               <div>
                 <h3 className="text-xl font-bold mb-4">3. How We Use Your Information</h3>
                 <p className="text-gray-700 mb-4">
                   We use the information we collect to:
                 </p>
                 <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                   <li>Provide, maintain, and improve our services</li>
                   <li>Process orders and facilitate payments</li>
                   <li>Connect customers with appropriate school bands</li>
                   <li>Send important service updates and notifications</li>
                   <li>Respond to customer support requests</li>
                   <li>Prevent fraud and ensure platform security</li>
                   <li>Comply with legal obligations</li>
                 </ul>
               </div>

               <div>
                 <h3 className="text-xl font-bold mb-4">4. Data Security</h3>
                 <p className="text-gray-700 mb-4">
                   We implement appropriate technical and organizational security measures to protect your personal 
                   information, including:
                 </p>
                 <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                   <li>Encryption of data in transit and at rest</li>
                   <li>Regular security assessments and updates</li>
                   <li>Access controls and authentication measures</li>
                   <li>Secure data centers and infrastructure</li>
                   <li>Employee training on data protection</li>
                 </ul>
               </div>

               <div>
                 <h3 className="text-xl font-bold mb-4">5. Your Privacy Rights</h3>
                 <p className="text-gray-700 mb-4">
                   Depending on your location, you may have the following rights regarding your personal information:
                 </p>
                 <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                   <li><strong>Access:</strong> Request a copy of your personal information</li>
                   <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                   <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                   <li><strong>Portability:</strong> Receive your data in a portable format</li>
                   <li><strong>Withdrawal:</strong> Withdraw consent for data processing</li>
                 </ul>
               </div>

               <div>
                 <h3 className="text-xl font-bold mb-4">6. Contact Information</h3>
                 <p className="text-gray-700 mb-4">
                   If you have any questions about this Privacy Policy or our data practices, please contact us:
                 </p>
                 <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                   <p className="text-gray-700"><strong>Email:</strong> privacy@bandcam.io</p>
                   <p className="text-gray-700"><strong>Phone:</strong> (555) 123-4567</p>
                   <p className="text-gray-700"><strong>Address:</strong> 123 Music Street, Suite 100, Anytown, ST 12345</p>
                   <p className="text-gray-700"><strong>Data Protection Officer:</strong> dpo@bandcam.io</p>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default BandSignupPage;
